import { SelectChangeEvent } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { CachePolicies } from "use-http";

import { Option } from "components/common/Autocomplete/Autocomplete";
import { JANZU_URL } from "config/config";
import { MAX_ANNOTATIONS_PER_IQ } from "config/constants";
import { useFetchCustom } from "hooks/useFetchCustom";
import { DetectorModes, detectorModesOptions } from "types/createDetector";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "types/detectors";
import { User } from "types/users";
import { addLeadingZeros } from "utils/math";

interface useCreateDetectorFormProps {
  numDetectors?: number;
  userInfo?: User;
}

export interface NewDetectorForm {
  labelPrompt: string;
  detectorName: string;
  groupOption?: Option<string>;
  groupName: string;
  answerMode: DetectorModes;
  maxCapCount: number;
  confidenceThreshold: number;
  validBboxLabels: string;
  class_names?: string;
  loading?: boolean;
  detectorCreationSettings?: object;
}

export const useCreateDetectorForm = (props: useCreateDetectorFormProps) => {
  const { numDetectors, userInfo } = props;

  const [currentStep, setCurrentStep] = useState(1);
  const [newDetectorForm, setNewDetectorForm] = useState<NewDetectorForm>({
    labelPrompt: "",
    detectorName: `${userInfo?.username}_detector_${addLeadingZeros(3, numDetectors || 0)}`,
    groupName: "",
    answerMode: detectorModesOptions[0].value,
    maxCapCount: MAX_ANNOTATIONS_PER_IQ,
    confidenceThreshold: DEFAULT_CONFIDENCE_THRESHOLD,
    validBboxLabels: "",
  });

  // Getting the mode settings (EX: MAX_COUNT_LIMIT)
  const predictorCreationSettingsUrl = `${JANZU_URL}reef-api/detectors/creation-settings?mode=${newDetectorForm.answerMode}`;

  const {
    data: detectorCreationSettings,
    get: getDetectorCreationSettings,
    loading,
  } = useFetchCustom(predictorCreationSettingsUrl, { cachePolicy: CachePolicies.NO_CACHE });

  const onSelectDetectorFormChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    if (name === "answerMode") {
      setNewDetectorForm((prev) => ({
        ...prev,
        [name]: value as DetectorModes,
        confidenceThreshold: value === DetectorModes.BINARY ? DEFAULT_CONFIDENCE_THRESHOLD : 0.9,
      }));
    } else {
      setNewDetectorForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    // This effect do two things:
    // 1. Get the detector creation settings from the BE
    // 2. Set the confidence threshold based on the answer mode

    if (newDetectorForm.answerMode) {
      getDetectorCreationSettings();
      handleConfidenceThresholdChange(DEFAULT_CONFIDENCE_THRESHOLD);
    }
  }, [getDetectorCreationSettings, newDetectorForm.answerMode]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;

    // When the className change for Count mode, the labelPrompt should change
    if (name === "validBboxLabels" && newDetectorForm.answerMode === DetectorModes.BOUNDING_BOX) {
      setNewDetectorForm((prev) => ({ ...prev, labelPrompt: `Draw a box around each ${value} in the image` }));
    }

    if (name === "validBboxLabels" && newDetectorForm.answerMode === DetectorModes.COUNT) {
      setNewDetectorForm((prev) => ({ ...prev, labelPrompt: `Label each ${value} in the image` }));
    }

    if (type === "checkbox") {
      const { checked } = event.target as HTMLInputElement;

      setNewDetectorForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === "number") {
      if (parseInt(value, 10) < 0) {
        return;
      }
      setNewDetectorForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setNewDetectorForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const onGroupChange = (groupOption?: Option<string>) => {
    setNewDetectorForm((prev) => ({
      ...prev,
      groupOption,
    }));
  };

  const handleConfidenceThresholdChange = (newValue: number) => {
    setNewDetectorForm((prevState) => ({
      ...prevState,
      confidenceThreshold: newValue,
    }));
  };

  const handleChangeStep = (action: "back" | "next") => {
    if (action === "back") {
      setCurrentStep((prevStep) => prevStep - 1);
      return;
    }
    setCurrentStep((prevStep) => prevStep + 1);
  };

  return {
    currentStep,
    newDetectorForm,
    onSelectDetectorFormChange,
    onInputChange,
    handleConfidenceThresholdChange,
    setNewDetectorForm,
    handleChangeStep,
    loading,
    detectorCreationSettings,
    onGroupChange,
  };
};
